import TitleUpdater from '../../../components/common/TitleUpdater';
import { Button, Footer, TitleBar } from '../../../components';
import QueryErrorBoundary from '../../../components/error-handling';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';

import { TableColumnsType } from 'antd';
import { useNavigate } from 'react-router';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useQuery } from '@apollo/client';
import { GET_CYBER_LOCATION_LIST } from './queries';
import { LocationLevelType } from './types';
import { StatusDisplay } from '../components/StatusDisplay';

dayjs.extend(relativeTime);

type CyberLocationListResponse = {
  getCyberLocationList: LocationLevelType[];
};

const LocationLevelList = () => {
  const navigate = useNavigate();

  const {
    data: cyberLocationListData,
    loading,
    error,
  } = useQuery<CyberLocationListResponse>(GET_CYBER_LOCATION_LIST);

  const cyberLocationList = cyberLocationListData?.getCyberLocationList ?? [];

  const columns: TableColumnsType<LocationLevelType> = [
    {
      title: 'Locations',
      width: 200,
      dataIndex: 'locationName',
      key: 'locationName',
      fixed: 'left',
      sortOrder: 'ascend',
      sorter: (a, b) =>
        tableSortingMethods.string(a.locationName, b.locationName),
      render: (text: string) => (
        <span className="custom-table--column ml-10">{text}</span>
      ),
    },
    {
      title: 'Status',
      width: 120,
      dataIndex: 'lastManuallyUpdatedAt',
      key: 'lastManuallyUpdatedAt',
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.lastManuallyUpdatedAt,
          b.lastManuallyUpdatedAt,
        ),
      render: (lastManuallyUpdatedAt: string, record: LocationLevelType) => {
        // "lastManuallyUpdatedAt" is a special case where the user manually updated the location,
        // so we need to set the dirty state to true.
        const isManuallyUpdated = lastManuallyUpdatedAt !== null ? true : false;

        return (
          <StatusDisplay
            isDirty={record.isDirty}
            isManuallyUpdated={isManuallyUpdated}
            lastUpdated={lastManuallyUpdatedAt || ''}
          />
        );
      },
    },
    {
      title: '# of Applications Mapped',
      width: 120,
      dataIndex: 'applictionsMapped',
      key: 'applictionsMapped',
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(a.applictionsMapped, b.applictionsMapped),
      render: (text: number) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  const extendActions = (record: LocationLevelType) => {
    const { locationUUID } = record || {};

    return (
      <Button
        type="primary-rounded"
        className="w-full text-sm font-semibold max-w-[100px]"
        onClick={() =>
          navigate(`/cyber/location-level/${locationUUID}/options`)
        }
      >
        Visit
      </Button>
    );
  };

  return (
    <>
      <TitleUpdater title="Location Level Applications" />
      <TitleBar
        title="Location Application Mapping"
        subtitle="Location Overview"
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={cyberLocationList}
          useBorderedSection
          loading={loading}
          editable
          editableOptions={{
            update: false,
          }}
          extendActions={extendActions}
          nonSearchableColumns={['locationUUID', 'lastUpdated']}
        />
      </QueryErrorBoundary>
      <Button
        type="primary"
        className="w-full text-sm font-bold max-w-[188px] mx-auto mt-7 py-2"
        onClick={() => navigate('/cyber/location-level/locations/selection')}
      >
        Add Location
      </Button>
      <Footer backPath={'/cyber'} />
    </>
  );
};

export default LocationLevelList;
